import { Map, List, fromJS } from 'immutable';
import { handleActions } from 'redux-actions';
import i18n from 'i18n.js';
import {
  CHANGE_TREND_TAG,
  CHANGE_RANK_TAG,
  CHANGE_PAGE_TYPE,
  CHANGE_SUMMARY_VM_SELECT_VALUE,
  GETSELECTOR_GET_SUCC,
  GET_TREND_SUCC,
  CHANGE_TREND_PARAMS,
  CHANGE_TOP3_STATUS,
  GET_HOURLY_STAT_SUCC,
  APP_RATE_SUCC,
  APP_ACCESS_SUCC,
  SYSTEM_RATE_SUCC,
  SYSTEM_ACCESS_SUCC,
  MODEL_ACCESS_SUCC,
  MODEL_RATE_SUCC,
  TAG_ACCESS_SUCC,
  TAG_RATE_SUCC,
  GPU_RATE_SUCC,
  GPU_ACCESS_SUCC,
  CPU_RATE_SUCC,
  CPU_ACCESS_SUCC,
  GPUDRIVERVERSION_RATE_SUCC,
  GPUDRIVERVERSION_ACCESS_SUCC,
  RESET_SEARCH_PARAMS,
  CHANGE_STATE_SUCC,
  CHANGE_DATA_TYPE,
  CHANGE_TOPISSUEDATA_TYPE,
  GET_APPEND_MINUTELY_STAT_SUCC,
  LOCATION_CHANGE,
} from 'store/actions';
import { getNickName, getUserId, parseSearch, tryToGetAppIdFromPathname, decodeURIComponentIgnoreError } from 'utils/helper';
import { getChartTags } from 'utils/StringUtils';
import {
  initialState,
  makeInitialSummarySearchParams,
  makeSummaryInitialSelectOptions,
} from './summaryInitialState';
import { isNullish } from 'utils/nullish';
import { getTreeSelectVersionOptions, mapTreeSelectOptions, renderLeafTitle } from 'utils/getTreeSelectVersionOptions';
import { VersionUtil } from 'utils/version-util';
import { ReduxActionType } from 'reducers/redux-action-type';
import { ExceptionCategoryUtil } from 'utils/exception-category';
import uniqBy from 'lodash.uniqby';
import { getAllInOneReduxState } from 'index';
import { ServerAppSettings } from 'utils/server-app-settings';
import { VmTypeInt } from 'utils/constants/vm-type';
import { message } from 'antd';


/**
 * 异常概览页版本下拉的业务定制逻辑：getTreeSelectVersionOptions 生成树时，节点的 value/title 都是基于
 * encode 后的字符串拼出来的（因为 clickhouse 底表存的版本号是 encode 过的，这是历史遗留问题，参见
 * GETSELECTOR_GET_SUCC 里 versionOptions 的 value 字段），所以未经处理的树在下拉框里会把版本号显示成
 * encode 后的样子（比如空格显示成 %20、中文显示成 %E4%B8%AD...）。
 *
 * 这里在树生成完毕之后，对每个节点的展示文案（title）做一次装饰：
 * 1. 如果节点的 value 能在 valueToLabel 里查到（说明这是一个真实的叶子版本号），就用查到的 label
 *    展示（label 允许携带 decode 还原不出来的额外信息，目前 GETSELECTOR_GET_SUCC 里直接用的是
 *    未做任何加工的版本号原文）；
 * 2. 查不到（说明这是中间节点 / 带通配符的聚合节点，不对应任何一个真实版本号），就用
 *    decodeURIComponentIgnoreError 兜底还原展示文案——因为 encodeURIComponent 不会转义 "."，
 *    中间节点按 "." 分段拼出来的 value 前缀，decode 之后同样能正确还原出对应的原始文本片段；
 * 3. 脏版本合并出来的"其他"占位父节点（value 是 VersionUtil.DIRTY_DATA_PARENT_VALUE_PLACEHOLDER）
 *    title 是固定文案，不参与上面两条规则，保持原样跳过；
 * 4. 叶子节点如果带别名（node.versionAlias），需要用 renderLeafTitle 重新渲染，保留别名 Tag 的
 *    展示效果，只是把里面的版本号文案换成上面算出来的新文案。
 *
 * @param {Array} versionTree getTreeSelectVersionOptions 生成的树
 * @param {Object.<string, string>} valueToLabel 版本号 value(encode 后) -> label(展示文案) 的映射（纯对象，
 *   不用 Map，避免在本文件已经 `import { Map } from 'immutable'` 的情况下造成同名遮蔽、误用成 immutable.js 的 Map）
 * @returns {Array}
 */
function decorateVersionTreeWithDecodedLabels(versionTree, valueToLabel) {
  return mapTreeSelectOptions(versionTree, (node) => {
    if (node.value === VersionUtil.DIRTY_DATA_PARENT_VALUE_PLACEHOLDER) {
      return null;
    }
    const displayText = Object.prototype.hasOwnProperty.call(valueToLabel, node.value)
      ? valueToLabel[node.value]
      : decodeURIComponentIgnoreError(node.value);
    return {
      ...node,
      title: node.versionAlias ? renderLeafTitle(displayText, node.versionAlias) : displayText,
    };
  });
}

function locationChangeReducer(state, action) {
  const { location } = action.payload;
  const { pathname } = location;
  const query = parseSearch(location);
  const { pid } = query;
  if (isNullish(pid)) {
    console.error('Cannot get pid in location_change');
  }

  const prevPathname = state.get('summaryPagePrevLocationChangePathname');
  console.log('locationChangeReducer', state, pathname, query, prevPathname)
  state = state.set('summaryPagePrevLocationChangePathname', pathname);
  if (isMatchSummaryRoute(pathname)) {
    // TODO: 这样拿appId可能会有问题
    const appId = tryToGetAppIdFromPathname(pathname);

    // pathname发生改变，判定为页面发生切换，重置summary大盘的相关state
    if (prevPathname !== pathname) {
      const reduxState = getAllInOneReduxState();
      const serverAppSettings = reduxState.app.get('appIdToServerAppSettings')[appId] || {};
      const dashboardDefaultVmType = serverAppSettings[ServerAppSettings.keys.dashboardDefaultVmType] || VmTypeInt.ALL;
      const dashboardDefaultSubModuleId = serverAppSettings[ServerAppSettings.keys.dashboardDefaultSubModuleId] || '-1';
      state = state.set('searchParams', makeInitialSummarySearchParams(appId, pid, {
        dashboardDefaultVmType,
        dashboardDefaultSubModuleId,
      }))
        .set('selectOptions', makeSummaryInitialSelectOptions(pid))
        .set('realTimeTopIssueData', initialState.get('realTimeTopIssueData'))
        .set('topIssueData', initialState.get('topIssueData'))
        .set(SYSTEM_RATE_SUCC, fromJS([]))
        .set(SYSTEM_ACCESS_SUCC, fromJS([]))
        .set(MODEL_RATE_SUCC, fromJS([]))
        .set(MODEL_ACCESS_SUCC, fromJS([]))
        .set(APP_RATE_SUCC, fromJS([]))
        .set(APP_ACCESS_SUCC, fromJS([]))
        .set(TAG_RATE_SUCC, fromJS([]))
        .set(TAG_ACCESS_SUCC, fromJS([]))
        .set(GPU_RATE_SUCC, fromJS([]))
        .set(GPU_ACCESS_SUCC, fromJS([]))
        .set(CPU_RATE_SUCC, fromJS([]))
        .set(CPU_ACCESS_SUCC, fromJS([]))
        .set(GPUDRIVERVERSION_RATE_SUCC, fromJS([]))
        .set(GPUDRIVERVERSION_ACCESS_SUCC, fromJS([]));
    }

    // 修正表示成了string的number型字段
    [
      'isRealTime',
      'topIssueJankType',
    ].forEach(numberField => {
      if (query[numberField]) {
        query[numberField] = Number(query[numberField]);
      }
    });
    // 修正表示成了string的boolean型字段
    [
      'isIntegratedAppTrend',
      'isQueryVital',
      'enableRealtimePrevDayTrend',
      'enableRealtimePrevWeekTrend',
    ].forEach(booleanField => {
      if (query[booleanField]) {
        query[booleanField] = query[booleanField] === 'true' || !!Number(query[booleanField]);
      }
    });

    state = state.update('searchParams', (searchParams) => {
      if (Object.keys(query).length) {
        return searchParams.merge(query);
      } else {
        const reduxState = getAllInOneReduxState();
        const serverAppSettings = reduxState.app.get('appIdToServerAppSettings')[appId] || {};
        const dashboardDefaultVmType = serverAppSettings[ServerAppSettings.keys.dashboardDefaultVmType] || VmTypeInt.ALL;
        const dashboardDefaultSubModuleId = serverAppSettings[ServerAppSettings.keys.dashboardDefaultSubModuleId] || '-1';
        return makeInitialSummarySearchParams(appId, pid, {
          dashboardDefaultVmType,
          dashboardDefaultSubModuleId,
        });
      }
    });

    // 只用本次 URL query 里的 exceptionCategory 同步旧 pageType。
    // AEXIT 第三卡只改 redux pageType、不写 URL；merge 后的 searchParams.exceptionCategory
    // 仍是初始崩溃。改日期会 LOCATION_CHANGE，若读 merge 后的旧值会把 OOM 打回崩溃。
    if (Object.prototype.hasOwnProperty.call(query, 'exceptionCategory')) {
      const exceptionCategory = query.exceptionCategory;
      if (ExceptionCategoryUtil.isCrash(exceptionCategory)) {
        state = state.setIn(['selectOptions', 'pageType'], '崩溃');
      } else if (ExceptionCategoryUtil.isAnr(exceptionCategory)) {
        state = state.setIn(['selectOptions', 'pageType'], '卡顿');
      } else if (ExceptionCategoryUtil.isError(exceptionCategory)) {
        state = state.setIn(['selectOptions', 'pageType'], '错误');
      } else if (ExceptionCategoryUtil.isOom(exceptionCategory)) {
        state = state.setIn(['selectOptions', 'pageType'], 'oom');
      } else if (ExceptionCategoryUtil.isJank(exceptionCategory)) {
        state = state.setIn(['selectOptions', 'pageType'], 'JANK');
      } else if (ExceptionCategoryUtil.isHang(exceptionCategory)) {
        state = state.setIn(['selectOptions', 'pageType'], 'HANG');
      }
    }
  }

  return state;
}


export default handleActions({
  // 这里是调用了issueActiopn里面的方法getRecordSearchOptions
  [GETSELECTOR_GET_SUCC]: (state, action) => {
    console.log('这是一个summary的页面', state, action, 'test');
    const { versionList = [], processorList = [] } = action.response;
    const { pid } = action.params;

    const versionOptions = (versionList || []).map(x => ({
      ...x,
      // 之前这里会根据 x.enable 给版本号加上 "(Closed)" 后缀，但判定逻辑是错的，线上出现了
      // 明明还在用的版本被错误标成 (Closed) 的问题，所以直接去掉这个判断，版本号原样展示
      label: x.name,
      value: encodeURIComponent(x.name), // bugly的老逻辑，其实没有encode的必要，发请求的时候还要decode，不知道是什么意思
    }));

    // value 是 encode 过的，这里生成树之后再装饰一遍展示文案（label），
    // 避免下拉框选项把版本号里的空格、中文等特殊字符显示成 encode 后的样子
    // 注意：这里不用 `new Map(...)`，因为本文件顶部 `import { Map } from 'immutable'` 会把
    // 原生 Map 遮蔽掉，`new Map(...)` 实际会构造出 immutable.js 的 Map，而不是原生 Map
    const valueToLabel = versionOptions.reduce((dict, x) => {
      dict[x.value] = x.label;
      return dict;
    }, {});
    const versionTree = decorateVersionTreeWithDecodedLabels(getTreeSelectVersionOptions(versionOptions), valueToLabel);

    return state.update('selectOptions', (options) => options.merge({
      trendVersion: {
        options: (() => {
          return fromJS(versionOptions).unshift({
            label: i18n.t('versionOptions.全版本'),
            value: '-1',
          });
        })(),
        versionTree,
      },
      rankVersion: {
        options: (() => {
          return fromJS(versionOptions);
        })(),
        versionTree,
      },
      processor: {
        options: new List(uniqBy(processorList, x => getUserId(x))).map((item) => ({
          label: getNickName(item),
          value: encodeURIComponent(getUserId(item)),
          rtx: (item || {}).rtx,
        })),
      },
    }));
  },

  [CHANGE_TREND_TAG]: (state, action) => {
    return state.update('searchParams', (params) => params.set('trendTagName', action.payload));
  },

  [CHANGE_RANK_TAG]: (state, action) => {
    const tagName = action.payload;
    let tagType = 'appTag';
    if (tagName.startsWith('system')) {
      tagType = 'systemTag';
    } else if (tagName.startsWith('app')) {
      tagType = 'appTag';
    } else if (tagName.startsWith('model')) {
      tagType = 'modelTag';
    } else if (tagName.startsWith('gpuDriverVersion')) {
      tagType = 'gpuDriverVersionTag';
    } else if (tagName.startsWith('gpu')) {
      tagType = 'gpuTag';
    } else if (tagName.startsWith('cpu')) {
      tagType = 'cpuTag';
    } else if (tagName.startsWith('tag')) {
      tagType = 'tagTag';
    }
    return state.update('selectOptions', (options) => options.set(tagType, tagName));
  },

  [CHANGE_SUMMARY_VM_SELECT_VALUE]: (state, action) => {
    const v = action.payload;
    return state.update('searchParams', (o) => o.set('vmSelectValue', v));
  },

  [CHANGE_PAGE_TYPE]: (state, action) => state
    .update('selectOptions', (options) => options.set('pageType', action.payload)),

  [GET_TREND_SUCC]: (state, action) => {
    const trendDataList = action.response.data;
    return state.set(action.params.dataType, fromJS(trendDataList).filter((item) => !!item));
  },

  [GET_HOURLY_STAT_SUCC]: (state, action) => {
    const trendList = action.response.data; // CountryUtil.convertTrendCountryGroupIdToName(state, action.response.data);
    const data = fromJS(trendList);
    return state.set(action.params.dataType, data.filter((item) => !!item));
  },

  [GET_APPEND_MINUTELY_STAT_SUCC]: (state, action) => {
    const trendList = action.response.data; // CountryUtil.convertTrendCountryGroupIdToName(state, action.response.data);
    const data = fromJS(trendList);
    return state.set(action.params.dataType, data);
  },

  [CHANGE_TREND_PARAMS]: (state, action) => {
    const { type, value } = action.payload;
    return state.update('searchParams', (param) => param.set(type, type === 'isRealTime' ? parseInt(value) : value));
  },

  summaryReducerSetTopIssueData: (state, action) => {
    const v = action.payload;
    return state.set('topIssueData', new Map(v));
  },

  summaryReducerPatchMergedIssueIdToSubJankTopIssueData: (state, action) => {
    const v = action.payload;
    const issueId = action.issueId;
    return state.set('mergedIssueIdToSubJankTopIssueData', { ...state.get('mergedIssueIdToSubJankTopIssueData'), [issueId]: v });
  },

  summaryReducerClearMergedIssueIdToSubJankTopIssueData: (state, action) => {
    return state.set('mergedIssueIdToSubJankTopIssueData', {});
  },

  [CHANGE_TOP3_STATUS]: (state, action) => state.set('isShowTop3', action.payload),

  [CHANGE_DATA_TYPE]: (state, action) => {
    return state.update('searchParams', (params) => params.set('dataType', action.payload));
  },

  [CHANGE_TOPISSUEDATA_TYPE]: (state, action) => {
    return state.update('searchParams', (params) => params.set('topIssueDataType', action.payload));
  },

  [APP_RATE_SUCC]: (state, action) => saveRankData(state, action, 'version'),
  [APP_ACCESS_SUCC]: (state, action) => saveRankData(state, action, 'version'),
  [MODEL_RATE_SUCC]: (state, action) => saveRankData(state, action, 'model'),
  [MODEL_ACCESS_SUCC]: (state, action) => saveRankData(state, action, 'model'),
  [SYSTEM_RATE_SUCC]: (state, action) => saveRankData(state, action, 'osVersion'),
  [SYSTEM_ACCESS_SUCC]: (state, action) => saveRankData(state, action, 'osVersion'),
  [GPU_RATE_SUCC]: (state, action) => saveRankData(state, action, 'gpu'),
  [GPU_ACCESS_SUCC]: (state, action) => saveRankData(state, action, 'gpu'),
  [CPU_RATE_SUCC]: (state, action) => saveRankData(state, action, 'cpu'),
  [CPU_ACCESS_SUCC]: (state, action) => saveRankData(state, action, 'cpu'),
  [TAG_RATE_SUCC]: (state, action) => saveRankData(state, action, 'tag'),
  [TAG_ACCESS_SUCC]: (state, action) => saveRankData(state, action, 'tag'),
  [GPUDRIVERVERSION_RATE_SUCC]: (state, action) => saveRankData(state, action, 'gpuDriverVersion'),
  [GPUDRIVERVERSION_ACCESS_SUCC]: (state, action) => saveRankData(state, action, 'gpuDriverVersion'),

  REALTIMETOPISSUE_FETCH_SUCC: (state, action) => {
    return state.set('realTimeTopIssueData', new Map(action.json.data))
      .update('searchParams', (params) => params.set('realTimeTopIssueLoading', false));
  },

  [LOCATION_CHANGE]: (state, action) => locationChangeReducer(state, action),

  [RESET_SEARCH_PARAMS]: (state, action) => {
    const { appId, pid, exceptionType } = action.payload;
    const reduxState = getAllInOneReduxState();
    const serverAppSettings = reduxState.app.get('appIdToServerAppSettings')[appId] || {};
    const dashboardDefaultVmType = serverAppSettings[ServerAppSettings.keys.dashboardDefaultVmType] || VmTypeInt.ALL;
    const dashboardDefaultSubModuleId = serverAppSettings[ServerAppSettings.keys.dashboardDefaultSubModuleId] || '-1';
    return state.update('searchParams', () => makeInitialSummarySearchParams(appId, pid, {
      dashboardDefaultVmType,
      dashboardDefaultSubModuleId,
    }));
  },

  [ReduxActionType.resetAppRelatedStateWhenAppChanged]: (state, action) => {
    const { appId, platformId } = action.payload;
    const reduxState = getAllInOneReduxState();
    const serverAppSettings = reduxState.app.get('appIdToServerAppSettings')[appId] || {};
    const dashboardDefaultVmType = serverAppSettings[ServerAppSettings.keys.dashboardDefaultVmType] || VmTypeInt.ALL;
    const dashboardDefaultSubModuleId = serverAppSettings[ServerAppSettings.keys.dashboardDefaultSubModuleId] || '-1';
    let newState = state.update('searchParams', () => makeInitialSummarySearchParams(appId, platformId, {
      dashboardDefaultVmType,
      dashboardDefaultSubModuleId,
    }));
    // TODO: 这里通过触发一下LOCATION_CHANGE这个reducer实现设置query参数到searchParams里面
    action.payload.location = window.location;
    return locationChangeReducer(newState, action);
  },

  UPDATE_SUMMARY_SEARCH_PARAMS: (state, action) => {
    const v = action.payload;
    return state.update('searchParams', x => x.merge(v));
  },

  updateSummarySelectOptions: (state, action) => {
    const v = action.payload;
    return state.update('selectOptions', x => x.merge(v));
  },

  [ReduxActionType.patchSummaryState]: (state, action) => {
    const v = action.payload;
    return state.merge(v);
  },

  SET_COUNTRY_GROUP_CONFIG_MODAL_VISIBLE: (state, action) => {
    const v = action.payload;
    return state.set('countryGroupConfigModalVisible', v);
  },

  [CHANGE_STATE_SUCC]: (state, action) => {
    let newState = state;
    ['topIssueData', 'realTimeTopIssueData'].forEach((field) => {
      newState = newState.update(field, (topIssueData) => topIssueData.update('topIssueList', (topIssueList) => {
        if (!Array.isArray(topIssueList)) {
          return topIssueList;
        }
        return topIssueList.map((issue) => {
          if (issue.issueId === action.params.issueIds) {
            issue.issueInfo.assigneeList = action.params.assigneeWithoutLocalUserIdList;
            issue.state = action.params.status;
          }
          return issue;
        });
      }));
    });
    return newState;
  },

  COUNTRYGROUPCONFIG_FETCH_SUCC: (state, action) => {
    const v = action.json.data;
    return state.set('countryGroupConfigList', fromJS(v));
  },

  FETCHSDKINTEGRATEDAPPS_FETCH_SUCC: (state, action) => {
    const options = action.json.data.map(x => ({
      label: x.name,
      value: x.appId,
    }));
    return state.setIn(['selectOptions', 'integratedAppOptions'], options);
  },

  FETCHSDKINTEGRATEDAPPVERSIONS_FETCH_SUCC: (state, action) => {
    const { integratedAppId } = action.params;
    const options = action.json.data.map(x => ({
      label: x,
      value: x,
    })).filter(x => x.value !== '-1');
    const optionsWithAllVersion = [
      ...options,
      { label: '全版本', value: '-1' },
    ];
    return state.updateIn(['selectOptions', 'integratedAppIdToVersionOptions'], (integratedAppIdToVersionOptions) => {
        return {
          ...integratedAppIdToVersionOptions,
          [integratedAppId]: optionsWithAllVersion,
        };
      })
      .updateIn(['selectOptions', 'integratedAppIdToVersionTree'], (integratedAppIdToVersionTree) => {
        return {
          ...integratedAppIdToVersionTree,
          [integratedAppId]: getTreeSelectVersionOptions(options),
        };
      });
  },

  TAG_ADD_SUCC: (state, action) => handleAddOrDelTagSucc(state, action),
  TAG_DEL_SUCC: (state, action) => handleAddOrDelTagSucc(state, action),

}, initialState);


function handleAddOrDelTagSucc(state, action) {
  let newState = state;
  ['topIssueData', 'realTimeTopIssueData'].forEach((field) => {
    const topIssueList = newState.getIn([field, 'topIssueList']);
    if (!topIssueList) {
      return;
    }

    const issueId = action.params.issueId;
    const { tagInfoList } = action.response;

    const newTopIssueList = topIssueList.map(x => {
      if (x.issueId === issueId) {
        x.tags = tagInfoList;
        return x;
      } else {
        return x;
      }
    })

    newState = newState.setIn([field, 'topIssueList'], newTopIssueList);
  });
  return newState;
}

// 会用到summary相关state的路径
// 注意：MetricKit 分析页面也复用 summary 相关 state（searchParams / selectOptions / trendData / ...），
// 所以也需要把它的 URL 同步到 Redux。
function isMatchSummaryRoute(pathname) {
  return pathname.match(/crash-reporting\/(dashboard|regional-dashboard|oom|model-oom|metrickit|metrickit-crash|aexit)\/\w+/i);
}

function saveRankData(state, action, field) {
  let topStats = action.json.data;
  const requestType = action.params?.type;
  const sortByException = action.params?.sortByException;
  if (!requestType) {
    message.error('Save ranking data failed, request type is empty!');
  }
  const isJank = ExceptionCategoryUtil.isJank(requestType);
  topStats = topStats.map(x => ({
    ...x,
    crashUser: isJank ? x.exceptionNum : x.exceptionDevices, // 王者卡顿，按次数排序
    accessUser: (isJank && sortByException) ? x.accessNum : x.accessDevices, // 王者卡顿，用次数率代替设备率
    [field]: x.fieldValue,
  }));
  return state.set(action.type, topStats);
}
